import { useState } from "react";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { useFetch, fmtDate } from "@/lib/ui";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/pages/Commission";

export function AcceptanceToggle({ p, onSaved }) {
  const [open, setOpen] = useState(false);
  const next = !p.requires_acceptance;
  const save = async () => {
    try {
      await api.put(`/admin/policies/${p.slug}/acceptance`, { requires_acceptance: next });
      toast.success(next ? "Customers must now accept this policy" : "Acceptance no longer required");
      setOpen(false); onSaved();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <div className="flex items-center gap-2 text-sm">
      <Switch checked={p.requires_acceptance} onCheckedChange={() => setOpen(true)} data-testid={`policy-require-toggle-${p.slug}`} />
      <span>Customers must accept before ordering</span>
      <ConfirmDialog open={open} onCancel={() => setOpen(false)} onConfirm={save}
        title={next ? "Require acceptance?" : "Stop requiring acceptance?"}
        body={next ? `Customers who haven't accepted v${p.version} will be unable to check out until they accept it.${p.needs_owner_review ? " This policy is still marked as needing owner review." : ""}` : "Customers will no longer be blocked at checkout for this policy."} />
    </div>
  );
}

export function AcceptanceReport({ p }) {
  const [version, setVersion] = useState(p.version);
  const [page, setPage] = useState(1);
  const { data, loading } = useFetch(`/admin/policies/${p.slug}/acceptances?version=${version}&page=${page}&limit=10`, [version, page]);
  if (loading || !data) return <p className="text-xs text-muted-foreground">Loading acceptances…</p>;
  const pages = Math.max(1, Math.ceil(data.total / 10));
  return (
    <div className="mt-4 rounded-md border p-3" data-testid={`policy-acceptances-${p.slug}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm mb-2">
        <span className="font-medium">Accepted by</span>
        <span data-testid={`policy-accept-count-${p.slug}`}>{data.total} customer{data.total === 1 ? "" : "s"} on v{version}</span>
        <select className="border rounded px-2 py-1 text-xs ml-auto" value={version} onChange={(e) => { setVersion(Number(e.target.value)); setPage(1); }} data-testid={`policy-accept-version-${p.slug}`}>
          {Array.from({ length: p.version }, (_, i) => p.version - i).map((v) => {
            const c = data.by_version.find((b) => b.version === v)?.count || 0;
            return <option key={v} value={v}>v{v} — {c}</option>;
          })}
        </select>
      </div>
      {data.customers.length === 0 ? <p className="text-xs text-muted-foreground" data-testid={`policy-accept-empty-${p.slug}`}>No customer has accepted this version yet.</p> : (
        <table className="w-full text-xs"><tbody>
          {data.customers.map((c) => <tr key={c.user_id} className="border-t"><td className="py-1.5">{c.name || "—"}</td><td>{c.email || c.phone || "—"}</td><td className="text-right text-muted-foreground">{fmtDate(c.accepted_at)}</td></tr>)}
        </tbody></table>)}
      {pages > 1 && <div className="flex justify-end gap-2 mt-2">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid={`policy-accept-prev-${p.slug}`}>Prev</Button>
        <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid={`policy-accept-next-${p.slug}`}>Next</Button></div>}
    </div>
  );
}
